import NoImage from 'assets/images/noimage.png'
import PropTypes from 'prop-types'
import { AlertCircle, CheckCircle, MinusCircle } from 'react-feather'
import colors from 'Theme/colors'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import {
  OPERATOR_OP_OPTIONS,
  evaluateRule,
  generateHumanReadableSummary,
} from 'features/private-views/map-schema/schemaRuleUtils'

const findOption = (options, value) => options.find((o) => o.value === value) || options[0]
const fmt = (n) => newFormatThousandSeparator(n, 3)
// Amounts are kept to 3 decimals; rounding avoids float sums like 13.2 + 6.6 = 19.799999999999997
const round3 = (n) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000

// Amount actually sourced from inventory (entries without an inventory are only a placeholder need)
const allocatedOf = (recipe) => {
  if (!recipe) return 0
  if (
    typeof recipe.allocatedAmount === 'number' &&
    !Number.isNaN(recipe.allocatedAmount)
  ) {
    return recipe.allocatedAmount
  }
  return (recipe.entries || [])
    .filter((e) => e.inventoryId && Number(e.amount) > 0)
    .reduce((sum, e) => sum + Number(e.amount), 0)
}

const StatusIcon = ({ ok }) =>
  ok ? (
    <CheckCircle size={14} color={colors.success[600]} className="flex-shrink-0" />
  ) : (
    <AlertCircle size={14} color={colors.warning[600]} className="flex-shrink-0" />
  )

StatusIcon.propTypes = { ok: PropTypes.bool.isRequired }

// Plain-language wording so users don't have to decode the operator
const GROUP_INSTRUCTION = {
  AND: 'Wajib pakai semua bahan berikut',
  OR: 'Pakai satu bahan atau campuran',
  XOR: 'Pilih salah satu bahan saja',
}
const MEMBER_CONNECTOR = { AND: 'DAN', OR: 'ATAU / CAMPUR', XOR: 'ATAU' }
const GROUP_CONNECTOR = { AND: 'DAN', OR: 'ATAU', XOR: 'ATAU (PILIH SATU KELOMPOK)' }

const Connector = ({ label, strong = false }) => (
  <div className="d-flex align-items-center gap-2" role="separator" aria-label={label}>
    <div className="flex-grow-1" style={{ borderTop: `2px dashed ${colors.primary[100]}` }} />
    <span
      className="badge rounded-pill fw-bold"
      style={{
        background: strong ? colors.primary[600] : colors.primary[400],
        color: colors.white,
        fontSize: strong ? '12px' : '11px',
        padding: strong ? '6px 16px' : '4px 12px',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </span>
    <div className="flex-grow-1" style={{ borderTop: `2px dashed ${colors.primary[100]}` }} />
  </div>
)

Connector.propTypes = { label: PropTypes.string.isRequired, strong: PropTypes.bool }

// Substitutes (Atau / Salah satu) share one need: the group needs the largest member requirement once,
// split across members for Atau, taken by exactly one member for Salah satu.
const planGroup = (group, members, quantity) => {
  const shared = members.length > 1 && group.op !== 'AND'
  if (!shared) return { shared, members: members.map((m) => ({ ...m })) }

  const need = round3(Math.max(...members.map(({ recipe }) => Number(recipe.reqAmount || 0) * quantity)))
  const allocations = members.map(({ recipe }) => allocatedOf(recipe))
  const filled = round3(allocations.reduce((a, b) => a + b, 0))
  const usedBy = members.filter((_, i) => allocations[i] > 0).map(({ recipe }) => recipe.nameItem)

  const planned = members.map((member, i) => {
    const othersFilled = round3(filled - allocations[i])
    const open = allocations[i] === 0
    if (open && group.op === 'XOR' && usedBy.length) {
      return { ...member, disabledReason: `Tidak dipakai, sudah memakai ${usedBy.join(', ')}` }
    }
    if (open && need - othersFilled <= 0) {
      return { ...member, disabledReason: `Kebutuhan sudah terpenuhi oleh ${usedBy.join(', ')}` }
    }
    // Each substitute only needs what the others have not covered yet (Salah satu: the whole need)
    const requiredAmount = group.op === 'XOR' ? need : Math.max(round3(need - othersFilled), 0)
    return { ...member, open, recipe: { ...member.recipe, requiredAmount } }
  })
  return { shared, need, filled, members: planned }
}

const DisabledRecipe = ({ recipe, reason }) => (
  <div
    className="border rounded-3 p-2 px-3 d-flex align-items-center gap-2"
    style={{ background: colors.neutral[50], color: colors.neutral[500] }}
    aria-disabled="true"
  >
    <img
      src={recipe.linkFoto || recipe.image || NoImage}
      alt=""
      style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', opacity: 0.5 }}
    />
    <div className="flex-grow-1" style={{ minWidth: 0 }}>
      <div className="fw-semibold text-truncate" style={{ fontSize: '13px' }}>
        {recipe.nameItem || recipe.label}
      </div>
      <div style={{ fontSize: '12px' }}>{reason}</div>
    </div>
    <MinusCircle size={16} className="flex-shrink-0" />
  </div>
)

DisabledRecipe.propTypes = { recipe: PropTypes.object.isRequired, reason: PropTypes.string.isRequired }

// Calculation aid only (not a pass/fail target), so it stays neutral; pass/fail comes from the rule check
const NeedProgress = ({ filled, need, unit, missingNames }) => {
  const pct = need > 0 ? Math.min(100, (filled / need) * 100) : 0
  const remaining = round3(need - filled)
  return (
    <div className="d-flex flex-column gap-1">
      <div
        className="rounded-pill overflow-hidden"
        style={{ height: 6, background: colors.neutral[200] }}
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: colors.primary[300] }} />
      </div>
      <div className="text-muted" style={{ fontSize: '12px' }}>
        {`Perkiraan: terisi ${fmt(filled)} dari ${fmt(need)} ${unit}`}
        {remaining > 0 &&
          ` · sisa ${fmt(remaining)} ${unit}${missingNames ? ` bisa dari ${missingNames}` : ''}`}
      </div>
    </div>
  )
}

NeedProgress.propTypes = {
  filled: PropTypes.number.isRequired,
  need: PropTypes.number.isRequired,
  unit: PropTypes.string.isRequired,
  missingNames: PropTypes.string,
}

// Renders recipes grouped by the schema's AND/OR/XOR rule with live status; flat list when there is no rule
const RuleGroupedRecipes = ({
  rule,
  recipes,
  suppliedItemIds,
  quantity,
  renderRecipe,
  showErrors = false,
}) => {
  const indexed = recipes.map((recipe, index) => ({ recipe, index }))
  if (!rule) {
    const invalid = showErrors && suppliedItemIds.length === 0
    return (
      <div
        className={`d-flex flex-column gap-3 ${invalid ? 'rounded-3 p-2' : ''}`}
        style={invalid ? { border: `1.5px solid ${colors.error[500]}` } : undefined}
        data-invalid={invalid}
      >
        {indexed.map(({ recipe, index }) => renderRecipe(recipe, index, { isInvalid: invalid }))}
      </div>
    )
  }

  const verdict = evaluateRule(rule, suppliedItemIds)
  // A join conflict (e.g. Salah satu kelompok with several satisfied) has no single failing group, so flag them all
  const noGroupFailed = verdict.groups.every((g) => g.ok)
  const isGroupInvalid = (i) => showErrors && !verdict.ok && (noGroupFailed || !verdict.groups[i].ok)
  const nameOf = (id) => recipes.find((r) => Number(r.id) === Number(id))?.nameItem
  const summary = generateHumanReadableSummary(rule, nameOf)
  const grouped = new Set(rule.groups.flatMap((g) => g.itemIds))
  const others = indexed.filter(({ recipe }) => !grouped.has(Number(recipe.id)))
  const multi = rule.groups.length > 1

  const renderMember = ({ recipe, index }, isInvalid) => renderRecipe(recipe, index, { isInvalid })

  return (
    <div className="d-flex flex-column gap-3">
      <div
        className="p-2 px-3 rounded-3 d-flex align-items-start gap-2"
        style={{
          background: verdict.ok ? colors.success[25] : colors.warning[25],
          border: `1px solid ${verdict.ok ? colors.success[200] : colors.warning[200]}`,
          fontSize: '12px',
        }}
        data-testid="product-mapping-schema-rule-status"
      >
        <span className="mt-1">
          <StatusIcon ok={verdict.ok} />
        </span>
        <div>
          <div className="fw-semibold text-dark">Aturan skema: {summary.formula}</div>
          <div className="text-muted">{verdict.ok ? 'Aturan skema terpenuhi.' : verdict.message}</div>
        </div>
      </div>

      {rule.groups.map((group, groupIdx) => {
        const opt = findOption(OPERATOR_OP_OPTIONS, group.op)
        const members = indexed.filter(({ recipe }) => group.itemIds.includes(Number(recipe.id)))
        if (!members.length) return null
        const plan = planGroup(group, members, quantity)
        const openNames = plan.members
          .filter((m) => m.open && !m.disabledReason)
          .map((m) => m.recipe.nameItem)
          .join(' atau ')
        return (
          <div key={groupIdx} className="d-flex flex-column gap-3">
            {groupIdx > 0 && <Connector strong label={GROUP_CONNECTOR[rule.join] || GROUP_CONNECTOR.AND} />}
            <div
              className="border rounded-3 p-2 p-md-3 d-flex flex-column gap-2"
              style={{
                background: colors.neutral[25],
                ...(isGroupInvalid(groupIdx) && { border: `1.5px solid ${colors.error[500]}` }),
                borderLeft: `3px solid ${
                  isGroupInvalid(groupIdx)
                    ? colors.error[500]
                    : verdict.groups[groupIdx].ok
                      ? colors.success[500]
                      : colors.warning[400]
                }`,
              }}
              data-invalid={isGroupInvalid(groupIdx)}
            >
              <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '13px' }} title={opt.description}>
                <StatusIcon ok={verdict.groups[groupIdx].ok} />
                {multi && <span className="text-muted">Kelompok {groupIdx + 1} ·</span>}
                <span className="fw-bold text-dark">
                  {members.length > 1 ? GROUP_INSTRUCTION[group.op] || GROUP_INSTRUCTION.AND : 'Wajib dipakai'}
                </span>
              </div>
              {plan.shared && (
                <NeedProgress
                  filled={plan.filled}
                  need={plan.need}
                  unit={members[0]?.recipe.unit || 'Kg'}
                  missingNames={openNames}
                />
              )}
              {plan.members.map((member, i) => (
                <div key={`${member.recipe.id}-${member.index}`} className="d-flex flex-column gap-2">
                  {i > 0 && <Connector label={MEMBER_CONNECTOR[group.op] || MEMBER_CONNECTOR.AND} />}
                  {member.disabledReason ? (
                    <DisabledRecipe recipe={member.recipe} reason={member.disabledReason} />
                  ) : (
                    renderMember(member, isGroupInvalid(groupIdx))
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}

      {others.length > 0 && (
        <div className="d-flex flex-column gap-3">
          <div className="text-muted fw-semibold" style={{ fontSize: '12px' }}>
            Bahan lain (di luar aturan skema)
          </div>
          {others.map(({ recipe, index }) => renderRecipe(recipe, index))}
        </div>
      )}
    </div>
  )
}

RuleGroupedRecipes.propTypes = {
  rule: PropTypes.shape({
    join: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ op: PropTypes.string, itemIds: PropTypes.arrayOf(PropTypes.number) })),
  }),
  recipes: PropTypes.arrayOf(PropTypes.object).isRequired,
  suppliedItemIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])).isRequired,
  quantity: PropTypes.number.isRequired,
  renderRecipe: PropTypes.func.isRequired,
  showErrors: PropTypes.bool,
}

export default RuleGroupedRecipes
